import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import connectToRouter from 'js/lib/connectToRouter';
import type { InjectedRouter } from 'js/lib/connectToRouter';

import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';
import type { ProcessImportSyncResponse } from '@coursera/grpc-types-enterpriseuser/coursera/proto/enterpriseuser/bulkprograminvitations/v1/bulk_program_invitations_api';

import enterpriseAdminUser from 'bundles/enterprise-admin';
import ProgramInvitationsApiManager from 'bundles/enterprise-admin-program-users/utils/ProgramInvitationsApiManager';
import ChooseContractTable from 'bundles/enterprise-admin-users/components/ChooseContractTable';
import ChooseProgramTable from 'bundles/enterprise-admin-users/components/ChooseProgramTable';
import ImportLearnerResults from 'bundles/enterprise-admin-users/components/ImportLearnerResults';
import ImportLearnersCSVUpload from 'bundles/enterprise-admin-users/components/ImportLearnersCSVUpload';
import ImportLearnersPreview from 'bundles/enterprise-admin-users/components/ImportLearnersPreview';
import { ASYNCHRONOUS_IMPORT, IMPORT_LEARNERS_STEPS } from 'bundles/enterprise-admin-users/constants';
import type { ProgramScope } from 'bundles/enterprise-admin-users/contextUtils';
import { connectToProgramScope } from 'bundles/enterprise-admin-users/contextUtils';
import type {
  PropsForProgramsAndRostersByOrganization,
  PropsFromProgramsAndRosters,
} from 'bundles/enterprise-admin-users/graphqlUtils';
import { withProgramsAndRosters } from 'bundles/enterprise-admin-users/graphqlUtils';
import type CSVInvitationModel from 'bundles/enterprise-admin-users/models/CSVInvitation';
import type {
  ImportLearnersSteps,
  ImportProcessingTypes,
  Program,
  S3Location,
  UserRoster,
} from 'bundles/enterprise-admin-users/types';
import { BulkEmailConfirm } from 'bundles/enterprise-ui';
import Modal from 'bundles/phoenix/components/Modal';

import _t from 'i18n!nls/enterprise-admin-users';

import 'css!./__styles__/ImportLearnersModal';

const { UPLOAD, CHOOSE_CONTRACT, CHOOSE_PROGRAM, PREVIEW, CONFIRM, RESULTS } = IMPORT_LEARNERS_STEPS;

type State = {
  stepIndex: number;
  selectedRoster?: UserRoster;
  selectedProgram?: Program;
  s3Location?: S3Location;
  processingType?: ImportProcessingTypes;
  csvInvitation?: CSVInvitationModel;
  reportPath?: string;
  sendInvitations: boolean;
  hasSentInvitations?: boolean;
  file?: File;
  error?: string;
  processing: boolean;
};

type PropsFromCaller = {
  onAsyncImport: (pollingUrl?: string) => void;
  handleClose: () => void;
  onSyncImport: () => void;
  programs?: Program[];
  roster?: UserRoster;
  showMultipleContracts?: boolean;
  didAgreeToTerms?: boolean;
  onAgreementToTermsChange?: () => void;
  courseId?: string;
  onLearnersAdded?: (learners: number) => void;
  onDone?: (inviteCount: number, isInviteLearners: boolean) => void;
  onAutoEnroll?: () => Promise<void>;
} & PropsForProgramsAndRostersByOrganization;

type Props = PropsFromCaller & PropsFromProgramsAndRosters & ProgramScope & PropsFromRouter;

type PropsFromRouter = {
  router: InjectedRouter;
};

type Context = {
  executeMutation: $TSFixMe;
};

export class ImportLearnersModal extends React.Component<Props, State> {
  declare _apiManager: ProgramInvitationsApiManager | null;

  static contextTypes = {
    executeMutation: PropTypes.func,
  };

  declare context: LegacyContextType<typeof ImportLearnersModal.contextTypes>;

  constructor(props: Props, context: Context) {
    super(props, context);
    this._apiManager = null;
    this.state = {
      sendInvitations: true,
      stepIndex: 0,
      processing: false,
      selectedRoster: props.roster,
    };
  }

  componentDidMount = () => {
    this.setProgramIfSingleProgramScope();
  };

  componentDidUpdate = (prevProps: Props, prevState: State) => {
    const { loading } = this.props;
    if (prevProps.loading && !loading) {
      this.setProgramIfSingleProgramScope();
    }

    if (prevState.selectedProgram) {
      const { executeMutation } = this.context;
      this._apiManager = new ProgramInvitationsApiManager({
        naptime: { executeMutation },
        program: prevState.selectedProgram,
      });
    }
  };

  setProgramIfSingleProgramScope = () => {
    const {
      programScope: { isProgramScope, programIds },
      programs,
    } = this.props;
    const isSingleProgramScope = isProgramScope && programIds.length === 1;
    if (programs && isSingleProgramScope) {
      this.setProgram(programs[0]);
    }
  };

  handlePreviousStep = () => {
    const step = this.getStep();
    if (step === CHOOSE_CONTRACT) {
      this.setState((prevState) => ({ stepIndex: prevState.stepIndex - 1, selectedRoster: undefined }));
    } else if (step === UPLOAD) {
      this.setState((prevState) => ({
        stepIndex: prevState.stepIndex - 1,
        file: undefined,
        s3Location: undefined,
        processingType: undefined,
        csvInvitation: undefined,
      }));
    } else {
      this.setState((prevState) => ({ stepIndex: prevState.stepIndex - 1 }));
    }
  };

  handleNextStep = () => {
    this.setState((prevState) => ({
      stepIndex: prevState.stepIndex + 1,
    }));
  };

  handleImportResponse = (hasSentInvitations: boolean, reportPath?: string, returnPollingUrl?: string) => {
    const { processingType, sendInvitations } = this.state;
    if (processingType === ASYNCHRONOUS_IMPORT) {
      const { onAsyncImport } = this.props;
      onAsyncImport(returnPollingUrl);
    } else {
      this.setState((prevState) => ({
        reportPath,
        hasSentInvitations,
        // skip confirmation step if we skipped sending invitations
        stepIndex: sendInvitations ? prevState.stepIndex + 1 : prevState.stepIndex + 2,
        processing: false,
      }));
      const { onSyncImport } = this.props;
      onSyncImport();
    }
  };

  handlePreviewComplete = () => {
    if (this.state.sendInvitations) {
      this.handleNextStep();
      return;
    }

    return this.handleInvitationProcessing();
  };

  handleInvitationProcessing = async () => {
    try {
      const { courseId, onAutoEnroll } = this.props;
      if (courseId && onAutoEnroll) {
        await onAutoEnroll();
        await this.handleProcessing();
      } else {
        await this.handleProcessing();
      }
    } catch {
      this.setState({
        error: _t('Error occurred while importing. Please try again.'),
        processing: false,
      });
    }
  };

  handleProcessing = () => {
    this.setState({ processing: true });
    const { processingType, s3Location, selectedRoster, sendInvitations } = this.state;
    const { router } = this.props;

    if (!s3Location) {
      throw new Error('Cannot process import without s3Location.');
    }

    const s3Key = s3Location.key;
    const s3Bucket = s3Location.bucket;
    const rosterId = selectedRoster?.id ?? router?.location?.query?.rosterId;

    this.setState({ processing: true });

    if (processingType === ASYNCHRONOUS_IMPORT) {
      return this._apiManager
        ?.getProcessInvitationsAsynchronouslyPromise({
          sendInvitations,
          rosterId,
          s3Key,
          s3Bucket,
        })
        .then(async (response) => {
          if (response.ok) {
            try {
              const responseJson = await response.json();
              return this.handleImportResponse(sendInvitations, undefined, responseJson.presignedUrl);
            } catch {
              this.handleError();
            }
          } else {
            this.handleError();
          }
        })
        .catch(() => this.handleError());
    } else {
      return this._apiManager
        ?.getProcessInvitationsSynchronouslyPromise({
          sendInvitations,
          rosterId,
          s3Key,
          s3Bucket,
        })
        .then((response) => {
          if (response.ok) {
            return response.json().then((data: ProcessImportSyncResponse) => {
              return this.handleImportResponse(sendInvitations, data.reportPath);
            });
          } else {
            return response.json().then((_) => {
              const { errorCode } = JSON.parse(_?.message ?? '{}');
              let errorMessage = _t('Error occurred while importing. Please try again.');
              if (errorCode === 'INVALID_CSV_ENCODING') {
                errorMessage = _t('Please, check the encoding of the CSV file, including the presence of emojis.');
              }
              this.handleError(errorMessage);
            });
          }
        })
        .catch(() => this.handleError());
    }
  };

  handleError = (error?: string) => {
    this.setState({
      error: error ?? _t('Error occurred while importing. Please try again.'),
      processing: false,
    });
  };

  getStep = (): ImportLearnersSteps => {
    const { stepIndex } = this.state;
    let steps = [CHOOSE_PROGRAM, CHOOSE_CONTRACT, UPLOAD, PREVIEW, CONFIRM, RESULTS];
    const {
      showMultipleContracts,
      programScope: { isProgramScope, programIds },
    } = this.props;

    const selectedRosters = this.getSelectedRosters();
    const isDirectSale = enterpriseAdminUser.isStandardContractUser();

    const isSingleProgramScope = isProgramScope && programIds.length === 1;
    if (isSingleProgramScope) {
      steps = steps.filter((s) => s !== CHOOSE_PROGRAM);
    }
    if (!showMultipleContracts || (selectedRosters.length === 1 && !isDirectSale)) {
      steps = steps.filter((s) => s !== CHOOSE_CONTRACT);
    }
    return steps[stepIndex];
  };

  getSelectedRosters = (): UserRoster[] => {
    const { selectedProgram } = this.state;
    const userRosters = selectedProgram?.userRosters?.elements || [];
    return userRosters.filter((roster) => !!roster) as UserRoster[];
  };

  setFile = (file?: File) => {
    this.setState({ file });
  };

  setProgram = (selectedProgram: Program) => {
    this.setState({ selectedProgram });
  };

  setRoster = (selectedRoster: UserRoster) => {
    this.setState({ selectedRoster });
  };

  setUploadResults = (
    s3Location: S3Location,
    processingType: ImportProcessingTypes,
    csvInvitation: CSVInvitationModel
  ) => {
    const { onLearnersAdded } = this.props;
    this.setState({ s3Location, processingType, csvInvitation });
    if (onLearnersAdded && csvInvitation) {
      onLearnersAdded(csvInvitation.invitationCount);
    }
  };

  toggleSendInvitations = (): void => {
    this.setState((prevState) => ({ sendInvitations: !prevState.sendInvitations }));
  };

  render() {
    const { selectedProgram, selectedRoster, processingType, csvInvitation, reportPath, hasSentInvitations, file } =
      this.state;
    const { handleClose, programs, loading, didAgreeToTerms, onAgreementToTermsChange, courseId, onDone } = this.props;
    const { stepIndex, error, sendInvitations, processing } = this.state;
    const step = this.getStep();
    const inviteCount = csvInvitation?.invitationCount || 0;

    return (
      <div className="rc-ImportLearnersModal" data-testid="import-learners-modal">
        <Modal modalName={_t('Import Learners Modal')} handleClose={handleClose}>
          {loading && (
            <div className="c-loadingWrapper vertical-box align-items-absolute-center">
              <SvgLoaderSignal size={64} />
            </div>
          )}
          {!loading && step === CHOOSE_PROGRAM && (
            <ChooseProgramTable
              onContinue={this.handleNextStep}
              selectedProgram={selectedProgram}
              setProgram={this.setProgram}
              programs={programs}
              handleClose={handleClose}
            />
          )}
          {!loading && step === CHOOSE_CONTRACT && (
            <ChooseContractTable
              onContinue={this.handleNextStep}
              selectedRoster={selectedRoster}
              userRosters={this.getSelectedRosters()}
              setRoster={this.setRoster}
              handleClose={handleClose}
              onPreviousStep={stepIndex > 0 ? this.handlePreviousStep : undefined}
            />
          )}
          {selectedProgram && step === UPLOAD && (
            <ImportLearnersCSVUpload
              onContinue={this.handleNextStep}
              file={file}
              selectedRoster={selectedRoster}
              onPreviousStep={stepIndex > 0 ? this.handlePreviousStep : undefined}
              handleClose={handleClose}
              program={selectedProgram}
              setFile={this.setFile}
              setUploadResults={this.setUploadResults}
              didAgreeToTerms={didAgreeToTerms}
              onAgreementToTermsChange={onAgreementToTermsChange}
              courseId={courseId}
            />
          )}
          {step === PREVIEW && (
            <ImportLearnersPreview
              selectedRoster={selectedRoster}
              selectedProgram={selectedProgram}
              processingType={processingType}
              csvInvitation={csvInvitation}
              onPreviousStep={this.handlePreviousStep}
              handleClose={handleClose}
              onContinue={this.handlePreviewComplete}
              sendInvitations={sendInvitations}
              toggleSendInvitations={this.toggleSendInvitations}
              error={error}
            />
          )}
          {step === CONFIRM && (
            <BulkEmailConfirm
              overThresholdMessage={
                <FormattedMessage
                  message={_t(
                    'You are about to send invitation emails to every individual listed in the uploaded CSV file.'
                  )}
                />
              }
              underThresholdMessage={
                <FormattedMessage
                  message={_t(
                    'You are about to send invitation emails to every individual listed in the uploaded CSV file.'
                  )}
                />
              }
              handleClose={handleClose}
              onPreviousStep={this.handlePreviousStep}
              count={inviteCount}
              onSubmit={this.handleInvitationProcessing}
              processing={processing}
              error={error}
              trackingName="enterprise_bulk_send_learner_invites"
            />
          )}
          {step === RESULTS && (
            <ImportLearnerResults
              reportPath={reportPath}
              hasSentInvitations={hasSentInvitations}
              csvInvitation={csvInvitation}
              onDone={onDone ? () => onDone(inviteCount, sendInvitations) : handleClose}
            />
          )}
        </Modal>
      </div>
    );
  }
}

export default compose<Props, PropsFromCaller>(
  connectToProgramScope,
  withProgramsAndRosters,
  connectToRouter<Props, PropsFromRouter>((router) => ({
    router,
  }))
)(ImportLearnersModal);
